import { QueryClient } from '@tanstack/react-query';
import { LOAD_DIRECTION } from 'Constants/enums';
import {
  API_ENDPOINTS,
  EMPTY_TIMEUUID,
  MESSAGE_BACKFILL_RECURSION_LIMIT,
  MESSAGE_LOAD_LIMIT,
  NODE_ENV_LOCAL_OR_DEVELOPMENT,
  NODE_ENV_PRODUCTION,
  TIMEZONE_IDENTIFIER,
} from 'Constants/env';
import { STORE_CHAT_HTML, STORE_CHAT_RAW } from 'Constants/localstorage';
import { MessagesGetRequest } from 'Interfaces/apiDtos';
import { AxiosResponseT, ResultsCollection } from 'Interfaces/axiosResponse';
import localforage from 'localforage';
import { isEmpty } from 'lodash';
import {
  IObservableArray,
  ObservableMap,
  action,
  makeObservable,
  observable,
  runInAction,
  toJS,
  when,
} from 'mobx';
import type { IPromiseBasedObservable } from 'mobx-utils';
import { createTransformer, fromPromise } from 'mobx-utils';
import ConversationModel from 'Models/ConversationModel';
import { GroupedMessagesContainer } from 'Models/GroupedMessagesContainer';
import {
  IMessageDocument,
  IMessageGifPayload,
  IMessageModel,
  IMessageModelChat,
  IMessageModelSMS,
  IMessageReferences,
  MessageModel,
} from 'Models/MessageModel';
import { QUERY_KEY_FETCH_CONVERSATIONS } from 'Modules/contacts/requests';
import { fetchConversations } from 'Modules/conversation/conversations-list/requests/index.requests';
import { useConversationStore } from 'Modules/conversation/store/index.store';
import { useNotificationStore } from 'Modules/notification/store';
import moment from 'moment-timezone';
import { RootStore } from 'Stores/RootStore';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { extractLastLink, normalizeUrl } from 'Utils/linkUtils';
import { bugsnagClient } from 'Utils/logUtils';
import { getISOStringFromTimeUUID } from 'Utils/timeUUIDParser';
import { isNullOrUndefined } from 'Utils/typeGuards';
import { v1 as uuidv1 } from 'uuid';
import API from '~/api';
import { FORCE_POLLING } from '../constants/featureFlags';
import { ForcePollingFeatureFlags } from '../providers/PollingProvider/index.types';
import { BaseStore } from './BaseStore';

export class MessageStore extends BaseStore {
  #queryClient: QueryClient;
  constructor(rootStore: RootStore, queryClient: QueryClient) {
    super(rootStore);
    this.#queryClient = queryClient;
    makeObservable(this);
  }

  @observable public groupedMessagesByConversationMap = new Map<
    string,
    GroupedMessagesContainer
  >();

  /** Whether resources such as images/link previews are currently loading for the active Conversation */
  @observable public resourceLoadStatus = new ObservableMap<string>();

  /** Raw text of the Message currently being drafted, per Conversation Id */
  @observable public messageDraftRawMap = new ObservableMap<string>();
  /** Display HTML of the Message currently being drafted, per Conversation Id. These values should be generated using a `contenteditable` div's innerHTML */
  @observable public messageDraftHtmlMap = new ObservableMap<string>();

  /** Raw text of the Message currently being edited */
  @observable public editMessageDraftRaw = '';
  /** Display HTML of the Message currently being edited. These values should be generated using a `contenteditable` div's innerHTML */
  @observable public editMessageDraftHtml = '';

  /** Whether the `AtMentionMembers` list is open on the **new** message input area */
  @observable public createMessageMentionListOpen = false;
  /** When the `AtMentionMembers` list is open on **new** input area, track the highlighted `Participant` id. `null` if none selected. */
  @observable public createMessageMentionSelectedParticipantId: string = null;
  /** Text typed after an `@` symbol in the **new** message input area, used to filter the `ParticipantPerson` list in a Channel when the Mentions list is open.  */
  @observable public createMessageMentionFilter = '';

  /** Whether the `AtMentionMembers` list is open on the **edit** message input area */
  @observable public editMessageMentionListOpen = false;
  /** When the `AtMentionMembers` list is open on **edit** input area, track the highlighted `Participant` id. `null` if none selected.*/
  @observable public editMessageMentionSelectedParticipantId: string = null;
  /** Text typed after an `@` symbol in the **edit** message input area, used to filter the `ParticipantPerson` list in a Channel when the Mentions list is open.  */
  @observable public editMessageMentionFilter = '';
  /** Whether older `Message`s are being loaded in an attempt to backfill to the last read `Message` */
  @observable public isBackfillingToReadMessage = new ObservableMap<
    string,
    boolean
  >();

  /** Map containing `Conversation` `id` keys, and a PBO wrapping the latest request to load messages for each `Conversation` */
  @observable public messageByConvStatusMap = new Map<
    string,
    IPromiseBasedObservable<AxiosResponseT<ResultsCollection<MessageModel>>>
  >();

  /** Cache a list of Message `id`s that have been passed as the `SkipId` when requesting more Messages for a Conversation, in order to avoid duplicating requests. */
  @observable private loadMoreMessagesSkipIdsMap = new Map<
    string,
    IObservableArray<string>
  >();

  @observable
  private loadMoreConversationMessagesStatus: IPromiseBasedObservable<
    AxiosResponseT<ResultsCollection<MessageModel>>
  > = null;

  @observable private createMessagePostStatus: IPromiseBasedObservable<
    AxiosResponseT<MessageModel>
  > = null;

  @observable private editMessagePostStatus: IPromiseBasedObservable<
    AxiosResponseT<MessageModel>
  > = null;

  @observable private deleteMessagePostStatus: IPromiseBasedObservable<
    AxiosResponseT<MessageModel>
  > = null;

  /** Select `IPromiseBasedObservable<AxiosResponseT<ResultsCollection<Message>>>` for the given `Conversation` `id` */
  selectLoadMessagesStatusByConversationId = createTransformer(
    (conversationId: string) =>
      this.messageByConvStatusMap.has(conversationId)
        ? this.messageByConvStatusMap.get(conversationId)
        : fromPromise.resolve()
  );

  selectGroupedMessagesForConversation = createTransformer(
    (conversationId: string) => {
      return this.groupedMessagesByConversationMap.has(conversationId)
        ? this.groupedMessagesByConversationMap.get(conversationId)
        : null;
    }
  );

  /**
   * Select `Message.Id`s for a `Conversation` that have already been skipped over as part of a previous request to load more `Message`s.
   * Returns `null` if the `Conversation.Id` isn't in the map.
   */
  selectLoadMoreMessagesSkipIds = createTransformer(
    (conversationId: string) => {
      return this.loadMoreMessagesSkipIdsMap.has(conversationId)
        ? toJS(this.loadMoreMessagesSkipIdsMap.get(conversationId))
        : null;
    }
  );

  /* ----------------- EDIT MESSAGE CODE --------------- */
  /** If editing any `Message` (in the active `Conversation`), this is the `id`, otherwise it will be set to `undefined` */
  @observable
  isEditingMessageId: string;

  /** The `id` of the newest `Message` that was sent by the logged-in `Person` (in the active `Conversation`).  */
  @observable
  newestMessageOwnedByUserId: MessageModel;

  @action
  setNewestMessageOwnedByUserId = (conversationId: string) => {
    if (!this.groupedMessagesByConversationMap.has(conversationId)) {
      this.newestMessageOwnedByUserId = null;
      return;
    }
    const mostRecent = this.groupedMessagesByConversationMap
      .get(conversationId)
      .AllMessagesDescending.find(
        (m) => m.personId === this.rootStore.personStore.loggedInPersonId
      );
    if (
      !isNullOrUndefined(mostRecent) &&
      mostRecent.isDeleted !== true &&
      (mostRecent.sms === undefined || mostRecent.sms === null)
    ) {
      this.newestMessageOwnedByUserId = mostRecent;
    } else {
      this.newestMessageOwnedByUserId = null;
    }
  };

  @action
  setMessageDraftRaw = (conversationId: string, rawContent?: string) => {
    this.messageDraftRawMap.set(conversationId, rawContent);
  };

  getMessageDraftRaw = createTransformer((conversationId: string) => {
    if (this.messageDraftRawMap.has(conversationId)) {
      return this.messageDraftRawMap.get(conversationId);
    } else {
      return '';
    }
  });

  @action
  setMessageDraftHtml = (conversationId: string, htmlContent?: string) => {
    this.messageDraftHtmlMap.set(conversationId, htmlContent);
    this.setLocalStorageForDrafts();
  };

  @action
  public setLocalStorageForDrafts = () => {
    //serialize the map
    localforage.setItem(STORE_CHAT_HTML, toJS(this.messageDraftHtmlMap));
    //serialize the map
    localforage.setItem(STORE_CHAT_RAW, toJS(this.messageDraftRawMap));
  };

  getMessageDraftHtml = createTransformer((conversationId: string) => {
    if (this.messageDraftHtmlMap.has(conversationId)) {
      return this.messageDraftHtmlMap.get(conversationId);
    } else {
      return '';
    }
  });

  @action
  setEditMessageDraftRaw = (rawContent: string) => {
    this.editMessageDraftRaw = rawContent;
  };

  @action
  setEditMessageDraftHtml = (htmlContent: string) => {
    this.editMessageDraftHtml = htmlContent;
  };

  @action
  setIsEditingMessageId = (id: string) => {
    if (id === null) {
      this.isEditingMessageId = undefined;
    } else {
      this.isEditingMessageId = id;
    }
  };

  /**
   * Set whether the `AtMentionMembers` list is open on the **new** message input area.
   *
   * - Automatically deselects any selected Participant via `setCreateMessageMentionSelectedParticipantId` if `false`.
   * - Automatically clears the create message mention filter via `setCreateMessageMentionFilter` if `false`
   */
  @action
  setCreateMessageMentionListOpen = (open: boolean) => {
    this.createMessageMentionListOpen = open;
    if (!open) {
      this.setCreateMessageMentionSelectedParticipantId(null);
      this.setCreateMessageMentionFilter('');
    }
  };

  /**
   * Set whether the `AtMentionMembers` list is open on the **edit** message input area.
   *
   * - Automatically deselects any selected Participant via `setEditMessageMentionSelectedParticipantId` if `false`.
   * - Automatically clears the edit message mention filter via `setEditMessageMentionFilter` if `false`
   */
  @action
  setEditMessageMentionListOpen = (open: boolean) => {
    this.editMessageMentionListOpen = open;
    if (!open) {
      this.setEditMessageMentionSelectedParticipantId(null);
      this.setEditMessageMentionFilter('');
    }
  };

  /**
   * Set the highlighted `Participant` id when the `AtMentionMembers` list is open on **new** input area.
   *
   * Pass `'here'` to select @here
   *
   * Pass `null` if none selected.
   */
  @action
  setCreateMessageMentionSelectedParticipantId = (participantId: string) => {
    this.createMessageMentionSelectedParticipantId = participantId;
  };

  /**
   * Set the highlighted `Participant` id when the `AtMentionMembers` list is open on **edit** input area.
   *
   * Pass `'here'` to select @here
   *
   * Pass `null` if none selected.
   */
  @action
  setEditMessageMentionSelectedParticipantId = (participantId: string) => {
    this.editMessageMentionSelectedParticipantId = participantId;
  };

  /**
   * Set the text used to filter `ParticipantPerson`s in the **new** input area.
   * This text will be passed as `filterVal` to `ParticipantStore#selectFilteredOtherParticipantPersonsInCurrentConversation`.
   *
   * Pass `''` (empty string) to clear the filter.
   */
  @action
  setCreateMessageMentionFilter = (createFilterVal: string) => {
    this.createMessageMentionFilter = createFilterVal;
  };

  /**
   * Set the text used to filter `ParticipantPerson`s in the **edit** input area.
   * This text will be passed as `filterVal` to `ParticipantStore#selectFilteredOtherParticipantPersonsInCurrentConversation`.
   *
   * Pass `''` (empty string) to clear the filter.
   */
  @action
  setEditMessageMentionFilter = (editFilterVal: string) => {
    this.editMessageMentionFilter = editFilterVal;
  };

  /**
   * Set whether older `Message`s are being loaded in an attempt to backfill to the last read `Message` for a `Conversation`.
   * @param conversationId
   * @param isBackfilling
   */
  @action
  setIsBackfillingToReadMessage = (
    conversationId: string,
    isBackfilling: boolean
  ) => {
    this.isBackfillingToReadMessage.set(conversationId, isBackfilling);
  };

  /**
   * Select whether older `Message`s are being loaded in an attempt to backfill to the last read `Message` for a `Conversation`.
   * @param conversationId
   */
  selectIsBackfillingToReadMessage = createTransformer(
    (conversationId: string) => {
      return this.isBackfillingToReadMessage.has(conversationId)
        ? this.isBackfillingToReadMessage.get(conversationId)
        : false;
    }
  );

  @action
  clearAllData = () => {
    this.groupedMessagesByConversationMap.clear();
    this.messageByConvStatusMap.clear();
    this.loadMoreMessagesSkipIdsMap.clear();
    this.createMessagePostStatus = null;
    this.editMessagePostStatus = null;
    this.deleteMessagePostStatus = null;
  };

  /**
   * Remove duplicate messages by ID.
   * Used to ensure no duplicate are left in the scenario where the pusher websocket message is received prior to the REST response to create the message (slow connection scenarios)
   * @param conversationId - The ID of the conversation the message belongs to.
   * @param messageId - The ID of the message to remove.
   * @param messageToKeep - The message to keep.
   */
  @action
  private removeDuplicateMessagesById = (conversationId: string, messageId: string, messageToKeep?: MessageModel) => {
    if (!messageId) {
      return;
    }

    const existingMsgGroup =
      this.groupedMessagesByConversationMap.get(conversationId);

    if (!existingMsgGroup) {
      return;
    }

    existingMsgGroup.groups.forEach((group) => {
      group.messages.replace(
        group.messages.filter(
          (currentMessage) =>
            currentMessage.id !== messageId ||
            currentMessage === messageToKeep
        )
      );
    });
  };

  @action
  editMessage = (
    conversationId: string,
    messageId: string,
    chat: IMessageModelChat
  ) => {
    const fileData = this.rootStore.uiStore.fileDeleteModal;
    const documents = fileData.externalId
      ? [{ externalId: fileData?.externalId, isDeleted: true }]
      : null;
    this.editMessagePostStatus = fromPromise(
      API.put(
        API_ENDPOINTS.ConversationMessageById(conversationId, messageId),
        { chat, documents }
      )
    );
    this.editMessagePostStatus.then(
      () => {
        // Find the message group and the specific message that is stored, update it's content
        const existingMsgGroup =
          this.groupedMessagesByConversationMap.get(conversationId);
        if (!existingMsgGroup) {
          bugsnagClient.notify('Error editing Message: no existingMsgGroup');
          useNotificationStore.getState().addNotify({
            type: 'error',
            dismissible: true,
            title: 'Error editing message',
            message: "The message wasn't edited. Please try again.",
          });
          return;
        }
        const message = existingMsgGroup.findMessageById(messageId);
        if (!message) {
          bugsnagClient.notify('Error editing Message: no message');
          useNotificationStore.getState().addNotify({
            type: 'error',
            dismissible: true,
            title: 'Error editing message',
            message: "The message wasn't edited. Please try again.",
          });
          return;
        }

        const oldContent =
          message.chat?.text_v2 || message.chat?.text || '';

        const newContent =
          chat.text_v2 || chat.text || '';

        const oldUrl = extractLastLink(oldContent);
        const newUrl = extractLastLink(newContent);

        if (chat !== null) {
          message.setChat(chat);
        }
        message.setUpdated(moment.tz(TIMEZONE_IDENTIFIER).toISOString());

        // Only clear preview if the last URL actually changed
        if (oldUrl !== newUrl && oldUrl !== null) {
          message.setLinkPreview(null);
        }
        message.setDocument(fileData.externalId);
        if (fileData.show) {
          this.rootStore.uiStore.setFileDeletePopup({
            ...fileData,
            show: false,
          });
        }
        const pinnedMessages = this.rootStore.uiStore.listOfPinnedMessages.get(conversationId);
        if (pinnedMessages) {
          runInAction(() => {
            const updatedPinned = pinnedMessages.map(pinnedMessage =>
              pinnedMessage.id === messageId
                ? { ...pinnedMessage, linkPreview: message.linkPreview }
                : pinnedMessage
            );
            this.rootStore.uiStore.listOfPinnedMessages.set(conversationId, updatedPinned);
          });
        }
      },
      (reason) =>
        useNotificationStore.getState().addNotify({
          type: 'error',
          dismissible: true,
          title: 'Error editing message',
          message: "The message wasn't edited. Please try again.",
        })
    );
    return this.editMessagePostStatus;
  };

  @action
  deleteMessage = (
    conversationId: string,
    messageId: string,
    deleteOnlyLocally = false
  ) => {
    if (deleteOnlyLocally) {
      void this.deleteLocalMessage(conversationId, messageId);
      this.setNewestMessageOwnedByUserId(conversationId);
      return;
    }

    this.deleteMessagePostStatus = fromPromise(
      API.delete(
        API_ENDPOINTS.ConversationMessageById(conversationId, messageId),
        {}
      )
    );

    this.deleteMessagePostStatus.then(
      () => {
        const existingMsgGroup =
          this.groupedMessagesByConversationMap.get(conversationId);
        if (existingMsgGroup !== undefined) {
          void this.deleteLocalMessage(conversationId, messageId);
          this.setNewestMessageOwnedByUserId(conversationId);
        }
      },
      () =>
        useNotificationStore.getState().addNotify({
          type: 'error',
          dismissible: true,
          title: 'Error deleting message',
          message: "The message wasn't deleted. Please try again.",
        })
    );
    return this.deleteMessagePostStatus;
  };
  /* ------------------------------------------------- */

  /* ------------------ LINK PREVIEW ------------------ */
  @action
  loadLinkPreview = async (
    url: string,
    conversationId: string,
    messageId: string
  ) => {
    // Check if the feature flag FORCE_POLLING is enabled
    const suppressErrors = this.rootStore.personStore.ldClient.variation(
      FORCE_POLLING,
      false
    ) as ForcePollingFeatureFlags;
    // Ignore silently attempts to load previews for SIP URLs
    if (url.startsWith('mailto:sip:') || url.startsWith('sip:')) {
      return false;
    }

    try {
      if (!conversationId || !messageId) {
        return false;
      }
      const normalizedUrl = normalizeUrl(url);

      // Check if link preview already exists for this message
      const existingMsgGroup =
        this.groupedMessagesByConversationMap.get(conversationId);
      const theMsg = existingMsgGroup?.findMessageById(messageId);

      if (!theMsg) {
        return false;
      }

      // Avoid unnecessary fetch if preview already exists for the same URL
      if (theMsg.linkPreview?.url) {
        const existingNormalized = normalizeUrl(theMsg.linkPreview.url);
        if (
          existingNormalized === normalizedUrl &&
          (theMsg.linkPreview.title || theMsg.linkPreview.description)
        ) {
          return true;
        }
      }

      if (!url?.startsWith('http://') && !url?.startsWith('https://')) {
        url = 'https://' + url;
      }

      const resp = await API.get(API_ENDPOINTS.LinkPreviewMetaGet(url));
      // Re-fetch message after await (race condition protection)
      const latestMsgGroup =
        this.groupedMessagesByConversationMap.get(conversationId);
      const latestMsg = latestMsgGroup?.findMessageById(messageId);

      if (!latestMsg) {
        return false;
      }

      // Validate message still contains the same URL
      const currentText =
        latestMsg.chat?.text_v2 || latestMsg.chat?.text || '';

      const currentLastUrl = extractLastLink(currentText);

      if (
        !currentLastUrl ||
        normalizeUrl(currentLastUrl) !== normalizedUrl
      ) {
        return false;
      }

      // Avoid empty previews for URLs that don’t provide title or description
      if (!resp.data.title && !resp.data.description) {
        return false;
      }

      const preview = {
        ...resp.data,
        url: resp.data.url || url,
      };

      latestMsg.setLinkPreview(preview);

      const pinnedMessages =
        this.rootStore.uiStore.listOfPinnedMessages.get(conversationId);
      if (pinnedMessages) {
        runInAction(() =>
          this.rootStore.uiStore.listOfPinnedMessages.set(
            conversationId,
            pinnedMessages.map((pinnedMessage) =>
              pinnedMessage.id === messageId
                ? {
                  ...pinnedMessage,
                  linkPreview: preview.url ? preview : null,
                }
                : pinnedMessage
            )
          )
        );
      }
      return true;
    } catch (reason) {
      bugsnagClient.notify(reason, (event) => {
        event.severity = 'error';
        event.context = 'loadLinkPreview';
        event.addMetadata('custom', {
          function: 'loadLinkPreview',
        });
      });

      // Only show a notification if forcePolling is not enabled
      if (!suppressErrors) {
        /* CUU2-2822
        useNotificationStore.getState().addNotify({
          type: 'error',
          dismissible: true,
          title: 'Error loading message',
          message: "We couldn't load the message. Please refresh the page.",
        }); */
      }

      return false;
    }
  };
  /* -------------------------------------------------- */

  @action
  loadConversationMessages = async (
    conversationId: string,
    messagesGetRequest?: MessagesGetRequest
  ) => {
    if (conversationId === EMPTY_TIMEUUID) {
      console.warn('loadConversationMessages aborted on EMPTY_TIMEUUID');
      return null;
    }
    if (NODE_ENV_LOCAL_OR_DEVELOPMENT) {
      console.debug(
        `loadConversationMessages for ${conversationId}`,
        messagesGetRequest
      );
    }
    await this.rootStore.personStore.waitUntilLoggedIn();

    // Create `GroupedMessages` entry immediately so it isn't undefined when `<ContextContentItemsList>` mounts/renders
    if (!this.groupedMessagesByConversationMap.has(conversationId)) {
      const grpMsgs = new GroupedMessagesContainer(
        conversationId,
        [],
        this.rootStore.participantStore.selectLoggedInUserParticipantLastReadMessageId
      );
      this.groupedMessagesByConversationMap.set(conversationId, grpMsgs);
    }
    const loadConvMsgsStatus = fromPromise(
      API.get(
        API_ENDPOINTS.ConversationMessages(conversationId, messagesGetRequest)
      )
    );
    loadConvMsgsStatus.then(
      (resp) => {
        this.loadConversationMessagesSuccess(
          conversationId,
          LOAD_DIRECTION.Initial,
          resp
        );
      },
      (reason) => {
        /* CUU2-2822
        useNotificationStore.getState().addNotify({
          type: 'error',
          dismissible: true,
          title: 'Error loading conversation messages',
          message:
            "We couldn't load the conversation messages. Please refresh the page.",
        }); */
      }
    );
    this.messageByConvStatusMap.set(conversationId, loadConvMsgsStatus);
    return loadConvMsgsStatus;
  };

  /**
   * Load Messages from the currently known newest Message, ex. upon re-connect
   */
  @action
  loadMessagesSinceNewestMessage = async (
    conversationId: string,
    newestMessageId: string
  ) => {
    const {
      rootStore: {
        preferenceStore,
        personStore,
        notificationStore,
        participantStore,
      },
    } = this;
    if (NODE_ENV_LOCAL_OR_DEVELOPMENT) {
      console.debug(
        `loadMoreConversationMessages for ${conversationId} || newestMessageId: ${newestMessageId}`
      );
    }
    await personStore.waitUntilLoggedIn();
    await preferenceStore.getExistingPreferenceData();
    // Create `GroupedMessages` entry immediately so it isn't undefined when `<ContextContentItemsList>` mounts/renders
    if (!this.groupedMessagesByConversationMap.has(conversationId)) {
      const grpMsgs = new GroupedMessagesContainer(
        conversationId,
        [],
        participantStore.selectLoggedInUserParticipantLastReadMessageId
      );
      this.groupedMessagesByConversationMap.set(conversationId, grpMsgs);
    }
    const msgsGet: MessagesGetRequest = {
      SkipId: newestMessageId,
      SkipOver: true,
      Limit: MESSAGE_LOAD_LIMIT,
      SortDirection: 'Ascending',
    };
    return when(() => personStore.IsLoggedIn, { timeout: 10000 })
      .then(() => {
        return preferenceStore.getExistingPreferenceData().then((resp) => {
          msgsGet.ShowCallMessagesInChat = resp.showCallMessagesInChat;
          const loadConvMsgsStatus = fromPromise(
            API.get(
              API_ENDPOINTS.ConversationMessagesAscending(
                conversationId,
                msgsGet
              )
            )
          );
          loadConvMsgsStatus.then(
            (resp) =>
              this.loadConversationMessagesSuccess(
                conversationId,
                LOAD_DIRECTION.Initial,
                resp
              ),
            (reason) => {
              /* CUU2-2822
              useNotificationStore.getState().addNotify({
                type: 'error',
                dismissible: true,
                title: 'Error loading conversation messages',
                message:
                  "We couldn't load the conversation messages. Please refresh the page.",
              }); */
            }
          );
          this.messageByConvStatusMap.set(conversationId, loadConvMsgsStatus);
          return loadConvMsgsStatus;
        });
      })
      .catch(() => {
        console.error(
          `loadMessagesSinceNewestMessage failed for ${conversationId} from Message ${newestMessageId}`
        );
      });
  };

  /** Load a single `Conversation` into `this.messageByConvStatusMap` only if the key (`conversationId`) is missing */
  @action
  loadConversationMessagesIfMissingGet = (
    conversationId: string,
    messagesGetRequest?: MessagesGetRequest
  ) => {
    if (NODE_ENV_LOCAL_OR_DEVELOPMENT) {
      console.debug(
        `loadConversationMessagesIfMissingGet for ${conversationId}`,
        messagesGetRequest
      );
    }
    if (!this.messageByConvStatusMap.has(conversationId)) {
      return this.loadConversationMessages(conversationId, messagesGetRequest);
    }
    this.setNewestMessageOwnedByUserId(conversationId);
    return this.messageByConvStatusMap.get(conversationId);
  };

  @action
  getFileDownloadLink = (fileId: string) => {
    const file = fromPromise<AxiosResponseT<any>>(
      API.get(API_ENDPOINTS.ConversationFile(fileId))
    );
    return file.then((resp) => resp);
  };

  @action
  loadMoreConversationMessages = async (
    conversationId: string,
    loadDirection: LOAD_DIRECTION,
    skipMessageId: string
  ) => {
    await this.rootStore.preferenceStore.getExistingPreferenceData();
    const msgsGet: MessagesGetRequest = {
      SkipId: skipMessageId,
      SkipOver: false,
      Limit: MESSAGE_LOAD_LIMIT,
      SortDirection:
        loadDirection !== LOAD_DIRECTION.Newer ? 'Descending' : 'Ascending',
      ShowCallMessagesInChat:
        this.rootStore.preferenceStore.preferences.showCallMessagesInChat,
    };
    const skipIdCacheVals = this.loadMoreMessagesSkipIdsMap.get(conversationId);

    // Don't load that which is already loaded, caching you will learn, young padawan.
    if (
      skipIdCacheVals !== undefined &&
      skipIdCacheVals.includes(skipMessageId)
    ) {
      return null;
    }
    if (skipIdCacheVals === undefined) {
      this.loadMoreMessagesSkipIdsMap.set(
        conversationId,
        observable.array([skipMessageId], { deep: false })
      );
    } else if (!skipIdCacheVals.includes(skipMessageId)) {
      skipIdCacheVals.push(skipMessageId);
    }
    if (NODE_ENV_LOCAL_OR_DEVELOPMENT) {
      console.debug(
        `loadMoreConversationMessages for ${conversationId}`,
        msgsGet,
        'skipIdCacheVals',
        toJS(skipIdCacheVals)
      );
    }
    const resp = await API.get(
      API_ENDPOINTS.ConversationMessages(conversationId, msgsGet)
    );
    return this.loadConversationMessagesSuccess(
      conversationId,
      loadDirection,
      resp
    );
  };

  @action
  loadConversationMessagesSuccess = async (
    conversationId: string,
    loadDirection: LOAD_DIRECTION,
    response: AxiosResponseT<ResultsCollection<IMessageModel>>
  ) => {
    if (NODE_ENV_LOCAL_OR_DEVELOPMENT) {
      console.debug(
        `loadConversationMessagesSuccess for ${conversationId}`,
        response.data
      );
    }
    const responseMsgs = response.data.results.map(
      MessageModel.FromResponseDto
    );
    if (loadDirection === LOAD_DIRECTION.Initial) {
      let grpMsgs: GroupedMessagesContainer;
      if (this.groupedMessagesByConversationMap.has(conversationId)) {
        grpMsgs = this.groupedMessagesByConversationMap.get(conversationId);
        responseMsgs.forEach((el) => {
          !!grpMsgs.findMessageById(el.id) && grpMsgs.deleteMessageById(el.id);
        });
        grpMsgs.pushNewerMessages(...responseMsgs);
      } else {
        console.warn(
          `GroupedMessages entry missing for Conversation ${conversationId} on initial load, creating it now...`
        );
        grpMsgs = new GroupedMessagesContainer(
          conversationId,
          responseMsgs,
          this.rootStore.participantStore.selectLoggedInUserParticipantLastReadMessageId
        );
        this.groupedMessagesByConversationMap.set(conversationId, grpMsgs);
      }
      this.setNewestMessageOwnedByUserId(conversationId);

      // Check if the logged-in user has a `readMessageId` for this Conversation
      try {
        await when(
          () =>
            this.rootStore.participantStore.lastReadByConvMap.has(
              conversationId
            ),
          { timeout: 10000 }
        );
        const readMessageId =
          this.rootStore.participantStore.lastReadByConvMap.get(conversationId);
        if (
          !isEmpty(readMessageId) &&
          !grpMsgs.selectContainsMessage(readMessageId) &&
          readMessageId !== EMPTY_TIMEUUID
        ) {
          if (!NODE_ENV_PRODUCTION) {
            console.debug(
              `MessageStore.loadConversationMessagesSuccess calling backfillMessages: (Conversation ${conversationId}) to readMessageId ${readMessageId}`,
              grpMsgs
            );
          }
          this.setIsBackfillingToReadMessage(conversationId, true);
          const conv =
            await this.rootStore.conversationStore.selectConversationById(
              conversationId
            );
          // Set the recursion limit to at least 3, but it can be higher if there are more Unread Messages past `MESSAGE_LOAD_LIMIT * 3`
          let backfillLimit = MESSAGE_BACKFILL_RECURSION_LIMIT;
          if (
            !isNullOrUndefined(conv) &&
            conv.data.unreadCount > MESSAGE_LOAD_LIMIT
          ) {
            backfillLimit = Math.max(
              Math.ceil(conv.data.unreadCount / MESSAGE_LOAD_LIMIT),
              backfillLimit
            );
          }
          return this.backfillMessages(
            conversationId,
            readMessageId,
            grpMsgs,
            backfillLimit
          ).then(
            (res) => {
              this.setIsBackfillingToReadMessage(conversationId, false);
              return res;
            },
            (err) => {
              bugsnagClient.notify(err, (event) => {
                event.context = 'MessageStore';
                event.severity = 'error';
                event.addMetadata('custom', {
                  function:
                    'loadConversationMessagesSuccess > backfillMessages',
                });
              });
              console.error(
                'loadConversationMessagesSuccess > backfillMessages error',
                err
              );
              return err;
            }
          );
        } else if (isEmpty(readMessageId) || readMessageId === EMPTY_TIMEUUID) {
          console.warn(
            `readMessageId was retrieved from the Map, but the value is Empty for Conversation ${conversationId}`
          );
        }
        return Promise.resolve(response);
      } catch (e) {
        if (!NODE_ENV_PRODUCTION) {
          console.debug(
            `loadConversationMessagesSuccess timed out before acquiring readMessageId for Conversation ${conversationId}. This is expected if the user had not previously read any messages in the Conversation.`
          );
        }
        return Promise.resolve(response);
      }
    } else {
      this.setIsBackfillingToReadMessage(conversationId, false); // Always false when not initial load
      let currGroupedMsgs =
        this.groupedMessagesByConversationMap.get(conversationId);
      // Fallback if not `LOAD_DIRECTION.Initial`, but there is no existing GroupedMessages instance (this should not happen, but will be recovered with a warning)
      if (currGroupedMsgs === undefined) {
        console.warn(
          `loadMoreConversationMessagesSuccess with loadDirection ${loadDirection} - this.groupedMessagesByConversationMap did not contain a GroupedMessages for Conversation ${conversationId}. One will be created, but this should not have happened.`
        );
        currGroupedMsgs = new GroupedMessagesContainer(
          conversationId,
          responseMsgs,
          this.rootStore.participantStore.selectLoggedInUserParticipantLastReadMessageId
        );
        this.groupedMessagesByConversationMap.set(
          conversationId,
          currGroupedMsgs
        );
        return Promise.resolve(response);
      }
      // `SortBy = Descending` (these start as newer -> older, and will be reversed by `GroupedMessages`, so they will go older -> newer)
      if (loadDirection === LOAD_DIRECTION.Older) {
        currGroupedMsgs.unshiftOlderMessages(...responseMsgs);
      } else {
        currGroupedMsgs.pushNewerMessages(...responseMsgs);
      }

      return Promise.resolve(response);
    }
  };

  @action
  backfillMessagesForAllConversations = async () => {
    if (NODE_ENV_LOCAL_OR_DEVELOPMENT) {
      console.debug(`backfillMessagesForAllConversations`);
    }
    await this.rootStore.personStore.waitUntilLoggedIn();
    const convIds = this.rootStore.conversationStore.conversationByIdMap.keys();
    const backfillPromises: Promise<AxiosResponseT<
      ResultsCollection<IMessageModel>
    > | void>[] = [];
    for (const c of Array.from(convIds)) {
      const lastReadMessageId =
        this.rootStore.participantStore.selectLoggedInUserParticipantLastReadMessageId(
          c
        );
      const grpMsgs = this.groupedMessagesByConversationMap.get(c);
      if (grpMsgs) {
        if (
          lastReadMessageId !== EMPTY_TIMEUUID &&
          !isEmpty(lastReadMessageId)
        ) {
          backfillPromises.push(
            this.backfillMessages(
              c,
              lastReadMessageId,
              grpMsgs,
              MESSAGE_BACKFILL_RECURSION_LIMIT
            )
          );
        }
      }
    }
    return Promise.all(backfillPromises);
  };

  /**
   * Load more Messages, attempting to recursively load until `readMessageId`.
   *
   * This method will return a `Promise` which is:
   * - Resolved with the last `AxiosResponseT<ResultsCollection<IMessageModel>>` response received if backfill was successful and not short-circuited
   * - Resolved with `false` if backfill was short-circuited due to `remainingBatchRequests` being 0
   * - Resolved with `null` if the `readMessageId` was already used as a `SkipId` for a previous request (to prevent duplicate requests)
   * - Rejected with the string "backfillMessages: groupedMessages was null or undefined!"
   * - Rejected with an error from `axios` if the request fails
   *
   * @private
   */
  @action
  private backfillMessages = (
    conversationId: string,
    readMessageId: string,
    groupedMessages: GroupedMessagesContainer,
    remainingBatchRequests: number
  ) => {
    const {
      rootStore: { personStore, preferenceStore, notificationStore },
    } = this;
    if (isNullOrUndefined(groupedMessages)) {
      return Promise.reject(
        'backfillMessages: groupedMessages was null or undefined!'
      );
    }
    if (remainingBatchRequests < 1) {
      console.debug(
        `backfillMessages short-circuited because remainingBatchRequests is 0.`
      );
      return Promise.resolve(false);
    }
    if (!NODE_ENV_PRODUCTION) {
      console.debug(
        `MessageStore.backfillMessages: (Conversation ${conversationId}) to readMessageId ${readMessageId}, remainingBatchRequests: ${remainingBatchRequests}`
      );
      if (!isNullOrUndefined(groupedMessages.OldestMessage)) {
        console.debug(
          `Current oldest Message: ${groupedMessages.OldestMessage.id
          } @ ${groupedMessages.OldestMessage.CreatedMoment.toISOString()}`
        );
      }
    }
    const skipIdCacheVals = this.loadMoreMessagesSkipIdsMap.get(conversationId);
    // Don't load that which is already loaded, caching you will learn, young padawan.
    if (
      skipIdCacheVals !== undefined &&
      skipIdCacheVals.includes(groupedMessages.OldestMessageId)
    ) {
      console.debug(
        `backfillMessages: Returning Promise.resolve(null) because skipIdCacheVals included ${groupedMessages.OldestMessageId} (Conversation Id ${conversationId})`
      );
      return Promise.resolve(null);
    }
    if (skipIdCacheVals === undefined) {
      this.loadMoreMessagesSkipIdsMap.set(
        conversationId,
        observable.array([groupedMessages.OldestMessageId], { deep: false })
      );
    } else if (!skipIdCacheVals.includes(groupedMessages.OldestMessageId)) {
      skipIdCacheVals.push(groupedMessages.OldestMessageId);
    }
    const msgsGet: MessagesGetRequest = {
      SkipId: groupedMessages.OldestMessageId,
      SkipOver: false,
      Limit: MESSAGE_LOAD_LIMIT,
      SortDirection: 'Descending',
      ShowCallMessagesInChat:
        preferenceStore.preferences.showCallMessagesInChat,
    };
    return preferenceStore.getExistingPreferenceData().then((resp) => {
      msgsGet.ShowCallMessagesInChat = resp.showCallMessagesInChat;
      return personStore.waitUntilLoggedIn().then(() => {
        return API.get(
          API_ENDPOINTS.ConversationMessages(conversationId, msgsGet)
        ).then(
          (resp: AxiosResponseT<ResultsCollection<IMessageModel>>) => {
            if (!isEmpty(resp.data.results)) {
              if (!NODE_ENV_PRODUCTION) {
                console.debug(
                  `MessageStore.backfillMessages: (Conversation ${conversationId}) resp.data.results is populated, continuing to backfillMessagesSuccess.`,
                  resp.data.results,
                  msgsGet
                );
              }
              return this.backfillMessagesSuccess(
                conversationId,
                readMessageId,
                groupedMessages,
                resp,
                remainingBatchRequests
              );
            }
            return resp;
          },
          (reason) => {
            /* CUU2-2822
            useNotificationStore.getState().addNotify({
              type: 'error',
              dismissible: true,
              title: 'Error when backfilling messages in conversation',
              message:
                "It wasn't possible to backfill the messages in conversation. Please try again.",
            }); */
            return reason;
          }
        );
      });
    });
  };

  @action
  private backfillMessagesSuccess = (
    conversationId: string,
    readMessageId: string,
    groupedMessages: GroupedMessagesContainer,
    response: AxiosResponseT<ResultsCollection<IMessageModel>>,
    remainingBatchRequests: number
  ) => {
    const responseMsgs = response.data.results.map(
      MessageModel.FromResponseDto
    );
    groupedMessages.unshiftOlderMessages(...responseMsgs);
    const readMessageIncluded = responseMsgs.some(
      (rm) => rm.id === readMessageId
    );
    if (!NODE_ENV_PRODUCTION) {
      console.debug(
        `backfillMessagesSuccess: readMessageIncluded ${readMessageIncluded} (Conversation ${conversationId})`
      );
    }
    // Continue if the Message still isn't present
    if (!readMessageIncluded && groupedMessages !== null) {
      if (!NODE_ENV_PRODUCTION) {
        console.debug(
          `backfillMessagesSuccess: Continuing because readMessageId ${readMessageId} was not found (Conversation ${conversationId})`
        );
      }
      // Recursion, decrement `remainingBatchRequests` to move towards short-circuit if necessary
      return this.backfillMessages(
        conversationId,
        readMessageId,
        groupedMessages,
        remainingBatchRequests - 1
      );
    } else if (!readMessageIncluded && isEmpty(response.data.nextId)) {
      console.info(
        `backfillMessagesSuccess: Short-circuiting backfill because Message ${readMessageId} was not included in the response, and there is no nextId.`
      );
    }
    return response;
  };

  /**
   * Retry sending a message.
   * This will delete the message from the local store and then create a new one.
   * @param conversation - The conversation to retry sending the message in.
   * @param message - The message to retry sending.
   */
  @action
  retryMessage = (conversation: ConversationModel, message: MessageModel) => {
    const existingMsgGroup = this.groupedMessagesByConversationMap.get(
      conversation.id
    );

    if (existingMsgGroup !== undefined) {
      const existingMsg = existingMsgGroup.findMessageById(message.id);

      if (existingMsg) {
        existingMsgGroup.deleteMessageById(message.id);
      }

      this.setNewestMessageOwnedByUserId(conversation.id);
    }

    void this.createMessagePost(
      conversation.id,
      message.gif ? undefined : message.chat,
      message.gif ? undefined : message.sms,
      conversation.grouping,
      message.documents,
      message.references && message.references.length > 0
        ? message.references[0]
        : undefined,
      message.gif
    );
  };

  /**
   * Resolves whether a Conversation is with an internal or external contact,
   * based on the Participant`s of the given Conversation.
   *
   * -Internal: all participants belong to the organization directory (have a personId).
   * -External: at least one participant is a saved contact, device contact, or a synchronized
   *  external account contact, identified by having a `phone` instead of a personId.
   *
   * @param conversationId ...
   * @returns `internal` if all participants are from the organization directory, `external` if
   * at least one participant is external, or `null` if participant data is unavailable
   */
  private resolveConversationContactType = (
    conversationId: string
  ): 'internal' | 'external' | null => {
    const otherParticipants =
      this.rootStore.participantStore.selectOtherParticipants(conversationId);

    if (!otherParticipants || otherParticipants.length === 0) {
      return null;
    }

    const hasExternal = otherParticipants.some((p) => !!p.phone);
    return hasExternal ? 'external' : 'internal';
  };

  /**
   * Add a (Chat or SMS) `Message` to a `Conversation`.
   *
   * Phone calls are separately handled via SIP.
   *
   * @param conversationId ...
   * @param [chat] ...
   * @param sms ...
   * @param conversationType ...
   * @param documents ...
   * @returns {IPromiseBasedObservable<AxiosResponseT<ResultsCollection<ParticipantModel>>>}
   */
  @action
  createMessagePost = (
    conversationId: string,
    chat?: IMessageModelChat,
    sms?: IMessageModelSMS,
    conversationType?: string,
    documents?: IMessageDocument[],
    references?: IMessageReferences,
    gif?: IMessageGifPayload
  ) => {
    const hasGif = Boolean(gif?.id);
    if (
      (!isEmpty(chat) && !isEmpty(sms)) ||
      (isEmpty(chat) && isEmpty(sms)) ||
      (!isEmpty(chat) && isEmpty(chat.text)) ||
      (!isEmpty(sms) && isEmpty(sms.text))
    ) {
      if (isEmpty(documents) && !hasGif) {
        return;
      }
    }
    const msg = new MessageModel({
      id: null,
      created: new Date().toISOString(),
      updated: null,
      personId: this.rootStore.personStore.loggedInPersonId,
      phone: null,
      chat: chat,
      gif,
      documents: documents,
      sms: sms,
      references: references ? [references] : undefined,
      internalStatus: chat || hasGif ? 'Sending' : undefined,
    });

    const existingMsgGroup =
      this.groupedMessagesByConversationMap.get(conversationId);

    if (documents) {
      this.rootStore.uiStore.setSendMessagePending(true);
    }

    // Locally-generated placeholder id  to be replaced by the authoritative
    // server UUID later. Mark the message pending so it is ordered by local
    // send sequence and pinned to the bottom until acknowledged.
    msg.setId(uuidv1());
    msg.markPending();

    if (existingMsgGroup !== undefined) {
      existingMsgGroup.pushMessageDirect(msg);
    } else {
      runInAction(() => {
        this.groupedMessagesByConversationMap.set(
          conversationId,
          new GroupedMessagesContainer(
            conversationId,
            [msg],
            this.rootStore.participantStore.selectLoggedInUserParticipantLastReadMessageId
          )
        );
      });
    }

    this.createMessagePostStatus = fromPromise(
      API.post(API_ENDPOINTS.ConversationMessages(conversationId), {
        ...(hasGif ? { gif } : { chat, sms }),
        documents,
        references: references?.id ? [references?.id] : null,
      })
    );

    this.createMessagePostStatus.then(
      ({ data: createdMsg }) => {
        // Adopt the server-authoritative id + created timestamp and re-position the
        // message by its server TimeUUID.
        if (existingMsgGroup) {
          existingMsgGroup.reconcileSentMessage(
            msg.id,
            createdMsg.id,
            createdMsg.created
          );
        } else {
          msg.acknowledgeServer(createdMsg.id, createdMsg.created);
        }
        this.removeDuplicateMessagesById(conversationId, createdMsg.id, msg);

        // Fire GA event to track whether the message was sent to an internal or external recipient
        const contactType = this.resolveConversationContactType(conversationId);
        if (contactType) {
          pushToGTMDataLayer(
            contactType === 'internal' ? 'message_internal_sent' : 'message_external_sent'
          );
        }

        if (this.rootStore.uiStore.selectedTopBarUsers) {
          this.rootStore.uiStore.setSelectedTopBarUsers(null);
        }

        // The message is already in the array from the initial pushMessageDirect call.
        // Only re-insert if the conversation messages weren't loaded yet (no existingMsgGroup).
        if (existingMsgGroup === undefined) {
          runInAction(() => {
            this.groupedMessagesByConversationMap.set(
              conversationId,
              new GroupedMessagesContainer(
                conversationId,
                [msg],
                this.rootStore.participantStore.selectLoggedInUserParticipantLastReadMessageId
              )
            );
          });
        }

        // Set internal status only if it's an internal message
        if (
          (chat || hasGif || (documents && documents.length > 0)) &&
          !sms
        ) {
          msg.setInternalStatus('Sent');
        }

        createdMsg?.chat?.smsRelay && msg.setSMSRelay(createdMsg.chat.smsRelay);
        createdMsg?.sms && msg.setSms(createdMsg.sms);
        createdMsg.documents && msg.setDocuments(createdMsg.documents);
        createdMsg?.references && msg.setReferences(createdMsg.references);
        createdMsg?.gif && msg.setGif(createdMsg.gif);
        if (hasGif && createdMsg?.chat) {
          msg.setChat(createdMsg.chat);
        }

        this.setNewestMessageOwnedByUserId(conversationId);
        const convPbo =
          this.rootStore.conversationStore.selectConversationById(
            conversationId
          );
        convPbo?.then((conv) => {
          conv.data.setLastMessageDate(createdMsg.created);
          conv.data.setLastMessageId(createdMsg.id);
        });
        this.rootStore.participantStore
          .updateMyLastReadMessage(conversationId, createdMsg.id)
          .then(() => {
            // Update the `readMessageId` with the newly assigned `id`
            this.rootStore.uiStore.setConversationAndTotalUnreadCount(
              conversationId,
              0,
              convPbo,
              0
            );
          });
        this.rootStore.uiStore.setSendMessagePending(false);

        // Check if we're in archived view and switch to all messages if so
        const currentFilter =
          useConversationStore.getState().conversationsListFilter;
        if (currentFilter === 'archived') {
          useConversationStore.getState().setConversationsListFilter('all');

          void this.#queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_FETCH_CONVERSATIONS],
          });

          void this.#queryClient.fetchQuery({
            queryKey: [QUERY_KEY_FETCH_CONVERSATIONS],
            queryFn: async () => {
              const conversations = await fetchConversations(this.rootStore);
              return conversations;
            },
          });
        }
      },
      (reason) => {
        runInAction(() => {
          msg.setFailedToSendSms(true);
          msg.setInternalStatus('FailedToSend');
        });

        pushToGTMDataLayer('sms_message_failed');

        this.rootStore.uiStore.setSendMessagePending(false);
        const code = reason?.response?.data?.code;
        if (['InboundSmsNotEnabled', 'NewSmsServiceError'].includes(code)) {
          this.rootStore.uiStore.setChatErrorSend(conversationId);
          return;
        }

        useNotificationStore.getState().addNotify({
          type: 'error',
          dismissible: true,
          title: 'Error sending message',
          message: "The message wasn't sent. Please try again.",
        });
      }
    );
    return this.createMessagePostStatus;
  };

  @action
  insertNewLocalOrPushMessage = (
    conversationId: string,
    message: MessageModel
  ) => {
    const {
      preferenceStore: { preferences },
    } = this.rootStore;
    if (isEmpty(message.created)) {
      message.created = getISOStringFromTimeUUID(message.id);
    }
    if (this.groupedMessagesByConversationMap.has(conversationId)) {
      const existingMsgGroup =
        this.groupedMessagesByConversationMap.get(conversationId);
      const existingMsg = existingMsgGroup.findMessageById(message?.id || '');
      !existingMsg &&
        (preferences.showCallMessagesInChat || !message.call) &&
        existingMsgGroup.pushMessageDirect(message);

      return fromPromise(Promise.resolve(null));
    } else {
      // Just load the messages, if they weren't loaded before, the new one should be there already (we know the user didn't send the message, because the messages for the Conversation weren't loaded)
      return this.loadConversationMessagesIfMissingGet(conversationId, {
        Limit: MESSAGE_LOAD_LIMIT,
        SortDirection: 'Descending',
        ShowCallMessagesInChat: preferences.showCallMessagesInChat,
      });
    }
  };

  @action
  editLocalMessage = (
    conversationId: string,
    message: MessageModel,
    ancestorId = '0',
    reactionsUpdated = false
  ) => {
    if (this.groupedMessagesByConversationMap.has(conversationId)) {
      const existingMsgGroup =
        this.groupedMessagesByConversationMap.get(conversationId);
      const existingMsg = existingMsgGroup.findMessageById(message.id);
      const ancestorMsg = existingMsgGroup.findMessageById(ancestorId);
      if (existingMsg) {
        existingMsg.setChat(message.chat);

        if (message.gif?.id) {
          existingMsg.setGif({ id: String(message.gif.id) });
        }

        // Update reactions if this is a reaction update
        if (reactionsUpdated && message.reactions !== undefined) {
          existingMsg.setReactions(message.reactions);
        }

        // Only set updated timestamp if it's not a reaction update
        if (!reactionsUpdated) {
          existingMsg.setUpdated(moment.tz(TIMEZONE_IDENTIFIER).toISOString());
        }

        pushToGTMDataLayer('editMessage', {
          conversationId,
        });
      }
      if (ancestorMsg) {
        ancestorMsg.setConference(message.conference);
      }
    } else {
      // Just load the messages, if they weren't loaded before, the new one should be there already (we know the user didn't send the message, because the messages for the Conversation weren't loaded)
      return this.loadConversationMessagesIfMissingGet(conversationId, {
        Limit: MESSAGE_LOAD_LIMIT,
        SortDirection: 'Descending',
        ShowCallMessagesInChat:
          this.rootStore.preferenceStore.preferences.showCallMessagesInChat,
      });
    }
  };

  @action
  deleteLocalMessage = (conversationId: string, messageId: string) => {
    if (this.groupedMessagesByConversationMap.has(conversationId)) {
      const existingMsgGroup =
        this.groupedMessagesByConversationMap.get(conversationId);
      const existingMsg = existingMsgGroup.findMessageById(messageId);
      if (existingMsg) {
        existingMsg.setChat({ text: '(Message Deleted)' });
        existingMsg.setDeleted(true);
        existingMsg.setUpdated('');
        existingMsg.setLinkPreview(null);
        existingMsg.setDocuments([]);
        existingMsg.setGif(undefined);
        pushToGTMDataLayer('deleteMessage', {
          conversationId,
        });
      }
    } else {
      // Just load the messages, if they weren't loaded before, the new one should be there already (we know the user didn't send the message, because the messages for the Conversation weren't loaded)
      return this.loadConversationMessagesIfMissingGet(conversationId, {
        Limit: MESSAGE_LOAD_LIMIT,
        SortDirection: 'Descending',
        ShowCallMessagesInChat:
          this.rootStore.preferenceStore.preferences.showCallMessagesInChat,
      });
    }
  };

  /**
   * Remove the `Message`s and any other data related to a `Conversation` from the store.
   *
   * Intended to handle `ConversationLeave` push notifications, should be called by `ConversationStore.removeLocalConversationData`
   */
  @action
  removeLocalConversationData = (conversationId: string) => {
    this.messageByConvStatusMap.delete(conversationId);
    this.groupedMessagesByConversationMap.delete(conversationId);
    this.loadMoreMessagesSkipIdsMap.delete(conversationId);
  };
}

export default MessageStore;
